import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { languageData } from '../constants'
import { ConfigProvider } from 'antd'

export const LanguageContext = createContext()

const defaultLanguage = {
  value: 'en',
  label: 'English',
  active: true
}

const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation()

  // Initialize state directly from localStorage to prevent flash of wrong language/direction
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const storedLang = localStorage.getItem('i18nextLng')
    if (storedLang && ['en', 'fr', 'ar'].includes(storedLang)) {
      const foundLang = Object.values(languageData).find((lang) => lang.value === storedLang)
      return foundLang || defaultLanguage
    }
    return defaultLanguage
  })

  // Set the direction immediately on mount and updates
  useEffect(() => {
    const dir = selectedLanguage.value === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.setAttribute('dir', dir)
  }, [selectedLanguage.value])

  useEffect(() => {
    // Sync i18n instance if it drifted (though usually handled by init)
    if (i18n.language !== selectedLanguage.value) {
      i18n.changeLanguage(selectedLanguage.value)
    }

    // Legacy check for first load - clean up if needed or keep for safety
    if (!localStorage.getItem('i18nextLng')) {
      localStorage.setItem('i18nextLng', defaultLanguage.value)
      i18n.changeLanguage(defaultLanguage.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateLanguage = useCallback(
    (newLang) => {
      if (['en', 'fr', 'ar'].includes(newLang.value)) {
        const updatedLanguages = Object.values(languageData).map((lang) => ({
          ...lang,
          active: lang.value === newLang.value
        }))
        const activeLang = updatedLanguages.find((lang) => lang.active)
        setSelectedLanguage(activeLang)
        i18n.changeLanguage(activeLang.value)
        localStorage.setItem('i18nextLng', activeLang.value)
      }
    },
    [i18n]
  )

  const direction = selectedLanguage.value === 'ar' ? 'rtl' : 'ltr'

  return (
    <LanguageContext.Provider value={{ selectedLanguage, updateLanguage }}>
      <ConfigProvider direction={direction}>{children}</ConfigProvider>
    </LanguageContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguageChanger = () => useContext(LanguageContext)
export default LanguageProvider
